import {
  StyledCartItem,
  CardProductImage,
  CartProductDescription,
  CartProductDescriptionMobile,
  CartProductHeader,
} from "./CartItem.styles";
import Text from "~/components/Text";
import { useCart } from "@shopify/hydrogen";
import { CartFragmentFragment } from "@shopify/hydrogen/dist/esnext/components/CartProvider/graphql/CartFragment";
import ProductPrice from "~/components/ProductPrice";
import GridLayout from "~/components/GridLayout";
import { useCallback, useEffect, useState, useRef } from "react";
import { useTheme } from "styled-components";
import { getProductByHandle } from "~/shopify";
import { useMediaQuery } from "styled-breakpoints/use-media-query";
import { Link, useNavigate } from "@remix-run/react";
import { ROUTES } from "~/routes/const";
import { LazyLoadImage } from "react-lazy-load-image-component";

export type CartItemProps = Pick<
  CartFragmentFragment["lines"]["edges"][1]["node"],
  "id" | "merchandise" | "quantity"
>;

const renderAsValidOption = ({
  name,
  value,
}: {
  name: string;
  value: string;
}) =>
  name.toLowerCase() === "title" || value.toLowerCase() === "default title"
    ? "One size"
    : value;

const CartItem = ({ id, merchandise, quantity }: CartItemProps) => {
  const theme = useTheme();
  const { linesRemove } = useCart();
  const productHandle = merchandise.product.handle;
  const [images, setImages] = useState<string[]>([]);
  const isDesktop = useMediaQuery(theme.breakpoints.up("desktop"));
  const navigate = useNavigate();

  useEffect(() => {
    getProductByHandle(productHandle, "NL").then((data) => {
      setImages(data.product.images.nodes.map((image) => image.url));
    });
  }, [productHandle]);

  const removeLine = useCallback(() => {
    linesRemove([id]);
  }, [id, linesRemove]);

  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(false);
  const [activeUrl, setActiveUrl] = useState(merchandise.image?.url);
  const intervalRunnerForImage = useRef<NodeJS.Timer>();
  const nextImageIndex = useRef<number>(0);

  useEffect(() => {
    if (isHovered) {
      intervalRunnerForImage.current = setInterval(() => {
        setActiveUrl(images[nextImageIndex.current]);

        if (nextImageIndex.current === images.length - 1) {
          nextImageIndex.current = 0;
        } else {
          nextImageIndex.current += 1;
        }
      }, 500);
    } else {
      setActiveUrl(merchandise.image?.url);
      clearInterval(intervalRunnerForImage.current);
    }
  }, [isHovered, merchandise, images]);

  return (
    <StyledCartItem
      onMouseEnter={() => setHoveredItem(true)}
      onMouseLeave={() => setHoveredItem(false)}
      onClick={() => {
        navigate(`${ROUTES.SHOP}/${productHandle}`);
      }}
    >
      <GridLayout>
        <CartProductHeader>
          <Text
            style={{
              textTransform: "uppercase",
              color: hoveredItem ? "white" : "black",
              ...(isDesktop && {
                marginBottom: theme.space[4],
              }),
            }}
            size="label-val"
          >
            DEPT APPAREL
          </Text>
          <Text
            as="button"
            onClick={removeLine}
            size="label-abc"
            style={{ color: hoveredItem ? "white" : "black" }}
          >
            X
          </Text>
        </CartProductHeader>
        <CardProductImage to={`${ROUTES.SHOP}/${productHandle}`}>
          <LazyLoadImage
            alt={merchandise.image?.altText || merchandise.product.title}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            src={activeUrl}
          />
        </CardProductImage>
        <CartProductDescription>
          <Link
            style={{
              gridColumn: "1 / -1",
              gridRow: 1,
              marginBottom: theme.space[8],
              ...(isDesktop && {
                gridColumn: "1 / 7",
                gridRow: 1,
                marginBottom: 0,
              }),
            }}
            to={`${ROUTES.SHOP}/${productHandle}`}
          >
            <Text
              as="span"
              size="label-abc"
              style={{ color: hoveredItem ? "white" : "black" }}
            >
              {merchandise.product.title}
            </Text>
          </Link>
          <Text
            style={{
              gridColumn: "6 / 8",
              gridRow: 1,
              textAlign: "right",
              color: hoveredItem ? "white" : "black",
            }}
            size="label-abc"
          >
            {renderAsValidOption(merchandise.selectedOptions[0])}
          </Text>
          <Text
            style={{
              gridColumn: "14",
              gridRow: 1,
              textAlign: "center",
              color: hoveredItem ? "white" : "black",
            }}
            size="label-abc"
          >
            {quantity}
          </Text>
          <Text
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: theme.space[4],
              gridColumn: "15 / span 4",
              gridRow: 1,
              justifyContent: "flex-end",
              color: hoveredItem ? "white" : "black",
            }}
            size="label-abc"
          >
            <ProductPrice
              compareAtPrice={merchandise.compareAtPriceV2}
              price={merchandise.priceV2}
              quantity={quantity}
            />
          </Text>
        </CartProductDescription>
        <CartProductDescriptionMobile
          onMouseEnter={() => setHoveredItem(true)}
          onMouseLeave={() => setHoveredItem(false)}
        >
          <Link
            style={{
              gridColumn: "1 / -1",
              gridRow: 1,
              marginBottom: theme.space[8],
            }}
            to={`${ROUTES.SHOP}/${productHandle}`}
          >
            <Text
              as="span"
              size="label-abc"
              style={{
                color: hoveredItem ? "white" : "black",
              }}
            >
              {merchandise.product.title}
            </Text>
          </Link>
          <Text
            style={{
              gridColumn: "1 / 3",
              gridRow: 2,
              marginBottom: theme.space[8],
              color: hoveredItem ? "white" : "black",
            }}
            size="label-abc"
          >
            {renderAsValidOption(merchandise.selectedOptions[0])}
          </Text>
          <Text
            style={{
              gridColumn: "3 / 4",
              gridRow: 2,
              marginBottom: theme.space[8],
              color: hoveredItem ? "white" : "black",
            }}
            size="label-abc"
          >
            {quantity}
          </Text>
          <Text
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: theme.space[4],
              gridColumn: "4 / span 3",
              gridRow: 2,
              justifyContent: "flex-end",
              marginBottom: theme.space[8],
              color: hoveredItem ? "white" : "black",
            }}
            size="label-abc"
          >
            <ProductPrice
              compareAtPrice={merchandise.compareAtPriceV2}
              price={merchandise.priceV2}
              quantity={quantity}
            />
          </Text>
        </CartProductDescriptionMobile>
      </GridLayout>
    </StyledCartItem>
  );
};

export default CartItem;
